import React, { useState, useEffect, useContext } from "react";
import { Tooltip, Image, Avatar, Button, Space } from "antd";
import { AppstoreOutlined } from "@ant-design/icons";

import { DateFormat, getApi, saveApi, successMessage, errorMessage } from "../../../helpers";
import CardContainer from "../../../components/Card";
import TableContainer from "../../../components/Table";
import CategoryPopup from "./Popup";
import CategorySidebar from "./Sidebar";
import { StoreContext } from "../../../context";
import { canWrite } from "../../../helpers/permissions";

const categoryColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: true,
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    ellipsis: {
      showTitle: true,
    },
  },
  {
    title: "Priority",
    dataIndex: "priority",
    key: "priority",
    align: "center",
    width: 100,
    sorter: true,
    render: (text) => (
      <span style={{ 
        padding: '4px 8px', 
        borderRadius: '4px', 
        backgroundColor: text ? '#f0f0f0' : 'transparent',
        fontWeight: text ? 600 : 'normal'
      }}>
        {text || '-'}
      </span>
    ),
  },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    render: (text) => DateFormat(text),
    sorter: true,
  },
  {
    title: "Image",
    dataIndex: "category_image",
    key: "category_image",
    align: "center",
    width: 100,
    render: (text) => (
      <Avatar
        shape="square"
        icon={<AppstoreOutlined />}
        size={50}
        alt=""
        src={text}
      />
    ),
  },
];

// category -> category_id,category_name,description,created_at,updated_at
// sub-category -> sub_category_id,sub_category_name,category_id,category_name,description,created_at,updated_at
export default function Category() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(8);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});
  const [sidebar, setSidebar] = useState(false);
  const [popup, setPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [savedPayload, setSavedPayload] = useState({});
  const [categorySections, setCategorySections] = useState([]);

  useEffect(() => {
    getList();
    getCategorySections();
  }, []);

  const getCategorySections = async () => {
    const res = await getApi(
      {
        key: "JWT",
        secret: "RAPSAP",
        type: "admin",
        keyword: "",
        page: 0,
        size: 100,
        sort: {
          field: "priority",
          order_by: "asc"
        }
      },
      "getCategorySection",
      "ecomm/"
    );
    if (res.data) {
      setCategorySections(res.data);
    }
  };

  const getList = async (payload = {}) => {
    setLoading(true);

    // Add priority-based default sorting if no sort is specified
    const requestPayload = {
      type: "admin",
      ...payload
    };

    // If no specific sort is provided, default to priority-based sorting
    if (!requestPayload.sort) {
      requestPayload.sort = {
        field: "priority",
        order_by: "asc"
      };
    }

    const res = await getApi(requestPayload, "getCategory");
    if (res.data) {
      setList(res.data);
      setTotal(res.meta?.total);
    }
    // Save the complete payload including page and size
    setSavedPayload(requestPayload);
    setLoading(false);
  };

    const handleTableChange = (pagination, filters, sorter) => {
    let payload = { ...savedPayload };

    // Update page and size from pagination
    if (pagination) {
      payload.page = pagination.current - 1;
      payload.size = pagination.pageSize;
    }

    if (sorter && sorter.field) {
      payload.sort = {
        field: sorter.field,
        order_by: sorter.order === 'ascend' ? 'asc' : 'desc'
      };
    } else {
      // If no sort is applied, use default priority sorting
      payload.sort = {
        field: "priority",
        order_by: "asc"
      };
    }

    getList(payload);
  };

  const saveHandler = async (values) => {
    setPopup(false);
    setSidebar(false);
    setLoading(true);

    try {
      // Step 1: Extract the image file from values
      const imageFile = values.file;
      delete values.file; // Remove file to avoid sending in JSON request

      // Step 2: Prepare the category data
      let categoryData = {
        name: values.name,
        description: values.description || "",
        priority: values.priority === "" ? null : parseInt(values.priority, 10) || null,
        is_active: values.is_active ? 1 : 0,
        category_section_id: values.category_section_id || null,
      };

      let apiName, msg;

      // For existing category update
      if (selected.category_id) {
        apiName = "updateCategory";
        msg = "Updated Category";
        categoryData.category_id = selected.category_id;
      }
      // For new category creation
      else {
        apiName = "createCategory";
        msg = "Added Category";
        categoryData.getId = true; // Flag to get back the category_id
      }

      // Step 3: Call API to create/update category
      const response = await saveApi(categoryData, msg, apiName);

      // Step 4: If successful and we have an image, upload it separately
      if (response && response.success === true && imageFile) {
        // Get category ID from response
        const categoryId = response.data.category_id;
        console.log("Category created with ID:", categoryId);

        // Create FormData for image upload
        const formData = new FormData();
        formData.append("category_id", categoryId);
        formData.append("category_img", imageFile);

        // Upload the image
        const headers = { "content-type": "multipart/form-data" };
        const imageResponse = await getApi(formData, "uploadCategoryImage", "ecomm/", headers);

        if (imageResponse.data) {
          successMessage("Category Image Uploaded");
        } else {
          errorMessage("Failed to upload image");
        }
      }

      // Refresh the category list
      getList(savedPayload);
    } catch (error) {
      console.error("Error in category save process:", error);
      errorMessage("Failed to save category");
      setLoading(false);
    }
  };

  return (
    <>
      {popup && (
        <CategoryPopup
          title={popupTitle}
          data={selected}
          visible={popup}
          cancelHandler={() => setPopup(false)}
          saveHandler={(values) => saveHandler(values)}
          uploadImage={selected.category_id ? false : true}
          categorySections={categorySections}
        />
      )}
      {sidebar && (
        <CategorySidebar
          data={selected}
          visible={sidebar}
          editCategory={() => (
            setPopupTitle("Edit Category Details"), setPopup(true)
          )}
          onUploadDone={() => (setSidebar(false), getList(savedPayload))}
          onClose={() => setSidebar(false)}
        />
      )}
      <CardContainer bordered={false} hoverable={false}>
        <TableContainer
          breadcrumbTitle="Category Management"
          loading={loading}
          total={total}
          columns={categoryColumns}
          data={list}
          searchFor="Category Name"
          addButton={canWrite() && (storeId ? false : true)}
          addHandler={() => {
            setSelected({});
            setPopupTitle("Add Category");
            setPopup(true);
          }}
          rowClickHandler={(record) => (
            setSelected(record),
            setSidebar(true)
          )}
          functionsHandler={getList}
          onChange={handleTableChange}
          scrollable={false}
        />
      </CardContainer>
    </>
  );
}
